import { useCallback, useState } from 'react';

import { FilteredNodes } from '@/features/canvas/contexts/editor-context';

interface GanttIsolationState {
  connectedConnectionIds: Set<string>;
  connectedInitiativeIds: Set<number>;
  isolatedInitiativeId: number | null;
}

interface UseGanttIsolationReturn {
  clearIsolation: () => void;
  isIsolationActive: boolean;
  isolateInitiative: (initiativeId: number) => void;
  isolationState: GanttIsolationState;
}

/**
 * Gantt-specific isolation hook - separate from Sunray dependency isolation
 * Handles click-to-isolate functionality for timeline bars
 */
export const useGanttIsolation = (allInitiatives: FilteredNodes[]): UseGanttIsolationReturn => {
  const [isolationState, setIsolationState] = useState<GanttIsolationState>({
    isolatedInitiativeId: null,
    connectedInitiativeIds: new Set(),
    connectedConnectionIds: new Set(),
  });

  /**
   * Collect all initiatives and connections related to the selected initiative
   */
  const collectConnectedDependencies = useCallback(
    (initiativeId: number): { connectionIds: Set<string>; initiativeIds: Set<number> } => {
      const initiativeIds = new Set<number>([initiativeId]);
      const connectionIds = new Set<string>();

      const targetInitiative = allInitiatives.find(
        initiative => initiative.id === initiativeId || initiative.initiatives?.id === initiativeId
      );

      if (!targetInitiative) {
        return { initiativeIds, connectionIds };
      }

      // Add both possible IDs for the target initiative
      if (targetInitiative.id) initiativeIds.add(targetInitiative.id);
      if (targetInitiative.initiatives?.id) initiativeIds.add(targetInitiative.initiatives.id);

      // Find outgoing dependencies (this initiative depends on others)
      (targetInitiative.dependencies || []).forEach(dep => {
        // Find the master node and add both its IDs
        const masterNode = allInitiatives.find(
          n => n.id === dep.masterNodeId || n.initiatives?.id === dep.masterNodeId
        );
        if (masterNode) {
          if (masterNode.id) initiativeIds.add(masterNode.id);
          if (masterNode.initiatives?.id) initiativeIds.add(masterNode.initiatives.id);
        }
        initiativeIds.add(dep.masterNodeId);
        connectionIds.add(`${dep.masterNodeId}-${dep.slaveNodeId}`);
      });

      // Find incoming dependencies (others depend on this initiative)
      allInitiatives.forEach(initiative => {
        (initiative.dependencies || []).forEach(dep => {
          if (
            dep.masterNodeId === initiativeId ||
            dep.masterNodeId === targetInitiative.initiatives?.id ||
            dep.masterNodeId === targetInitiative.id
          ) {
            // Find the slave node and add both its IDs
            const slaveNode = allInitiatives.find(
              n => n.id === dep.slaveNodeId || n.initiatives?.id === dep.slaveNodeId
            );
            if (slaveNode) {
              if (slaveNode.id) initiativeIds.add(slaveNode.id);
              if (slaveNode.initiatives?.id) initiativeIds.add(slaveNode.initiatives.id);
            }
            initiativeIds.add(dep.slaveNodeId);
            if (initiative.id) initiativeIds.add(initiative.id);
            if (initiative.initiatives?.id) initiativeIds.add(initiative.initiatives.id);
            connectionIds.add(`${dep.masterNodeId}-${dep.slaveNodeId}`);
          }
        });
      });

      return { initiativeIds, connectionIds };
    },
    [allInitiatives]
  );

  const isolateInitiative = useCallback(
    (initiativeId: number) => {
      setIsolationState(prev => {
        // If clicking the same initiative, clear isolation
        if (prev.isolatedInitiativeId === initiativeId) {
          return {
            isolatedInitiativeId: null,
            connectedInitiativeIds: new Set(),
            connectedConnectionIds: new Set(),
          };
        }

        // Otherwise, isolate the new initiative
        const { initiativeIds, connectionIds } = collectConnectedDependencies(initiativeId);

        return {
          isolatedInitiativeId: initiativeId,
          connectedInitiativeIds: initiativeIds,
          connectedConnectionIds: connectionIds,
        };
      });
    },
    [collectConnectedDependencies]
  );

  const clearIsolation = useCallback(() => {
    setIsolationState({
      isolatedInitiativeId: null,
      connectedInitiativeIds: new Set(),
      connectedConnectionIds: new Set(),
    });
  }, []);

  const isIsolationActive = isolationState.isolatedInitiativeId !== null;

  return {
    isolationState,
    isolateInitiative,
    clearIsolation,
    isIsolationActive,
  };
};
